// The account's achievements (ACHIEVEMENTS.md §11, §12; BACKLOG A4): the
// wall's read, and the moment's queue.
//
// THE WALL is `GET /api/me/achievements`, read when a screen that shows it
// opens and again whenever a find arrives, so a wall left open fills in.
//
// THE MOMENT comes from `unlocked` — what the account has not been shown — on
// `/api/me` and on the pulse the bell asks for on its rhythm, and at once after
// an action that earned something (`askMeNow`). A find is queued once per tab,
// taken when the screen is clear (no pick on the clock, no sheet open), and
// marked seen as it shows, which takes it off `/api/me` for every device. A
// tab that closes before it shows leaves it unseen, so it shows next time.

import { useEffect, useSyncExternalStore } from 'react';
import {
  AchievementsSeen,
  AchievementsView,
  type AchievementFeature,
  type FoundAchievement,
} from '@fsp/domain';
import { send } from './api';
import { onMe } from './meFeed';
import { useRead } from './read';
import { useSession } from './session';

// ── The queue ─────────────────────────────────────────────────────────────

interface Queue {
  userId: string;
  /** Not shown yet, newest first. */
  pending: FoundAchievement[];
}

let queue: Queue | null = null;
/** `${userId}|${id}`: shown in this tab, so a poll that crosses the seen mark does not show it again. */
const shown = new Set<string>();
/** Bumped by every find that arrives: the wall reads itself again. */
let arrivals = 0;
const listeners = new Set<() => void>();

function publish(next: Queue | null): void {
  queue = next;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

onMe((news) => {
  // AN ANSWER THAT DID NOT SAY IS NOT "NOTHING WAITS": a profile save answers
  // a `MeView` without `unlocked`, and reading it as empty would drop a find
  // waiting behind an open sheet until the next pulse (the review of the polls).
  if (news.unlocked === undefined) return;
  const userId = news.userId;
  const unseen = news.unlocked.filter((a) => !shown.has(`${userId}|${a.id}`));
  // WHAT ANOTHER DEVICE SHOWED LEAVES THE QUEUE: a find waiting here behind
  // an open sheet, and marked seen on the phone meanwhile, is no longer in the
  // answer, and must not show here once the sheet closes (the review of A4).
  const held = (queue?.userId === userId ? queue.pending : []).filter((h) =>
    unseen.some((a) => a.id === h.id),
  );
  const fresh = unseen.filter((a) => !held.some((h) => h.id === a.id));
  if (fresh.length === 0 && queue?.userId === userId && held.length === queue.pending.length)
    return;
  arrivals += fresh.length;
  publish({ userId, pending: [...fresh, ...held] });
});

/** What is waiting to be shown to the signed-in person. */
export function usePendingMoments(userId: string | null): FoundAchievement[] {
  const q = useSyncExternalStore(subscribe, () => queue);
  return q !== null && q.userId === userId ? q.pending : NONE;
}
const NONE: FoundAchievement[] = [];

/** How many finds have arrived in this tab: a wall that is open reads itself again. */
export function useArrivals(): number {
  useSyncExternalStore(subscribe, () => queue);
  return arrivals;
}

/** Take everything waiting, for one card, and mark it shown here. */
export function takeMoments(userId: string): FoundAchievement[] {
  if (queue === null || queue.userId !== userId || queue.pending.length === 0) return [];
  const taken = queue.pending;
  for (const a of taken) shown.add(`${userId}|${a.id}`);
  publish({ userId, pending: [] });
  return taken;
}

/**
 * Say they were shown, on any device (`POST /api/me/achievements/seen`). A
 * failure is quiet: they are shown here already, and the next device shows
 * them once more, which is the honest outcome of a lost request.
 */
export async function markSeen(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  await send('POST', '/me/achievements/seen', { ids }, AchievementsSeen);
}

// ── When the screen is clear ──────────────────────────────────────────────

let holds = 0;

/**
 * Hold the moment while this is true: a pick on the clock (§12, "it never
 * covers a pick while you are on the clock"). Released when it turns false or
 * the screen goes. Both draft rooms hold it while my pick is up: the
 * practice one and a real league's live one (D10).
 */
export function useHoldMoments(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    holds += 1;
    return () => {
      holds -= 1;
    };
  }, [active]);
}

/** Nothing on the clock, no sheet or dialog open, and the page in front of the person. */
export function momentsClear(): boolean {
  if (holds > 0) return false;
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  return document.querySelector('[role="dialog"]') === null;
}

/**
 * A card's turn on the screen (`Moments.tsx`): an achievement's and a
 * milestone's take turns rather than stack. `mayShow` is asked at the moment a
 * card would show; `onUp` says it has shown (`true`) or gone (`false`).
 */
export interface MomentTurn {
  mayShow: () => boolean;
  onUp: (up: boolean) => void;
}

/** Achievements waiting to be shown to this person: a milestone's card waits for them. */
export function momentsWaiting(userId: string | null): boolean {
  return queue !== null && queue.userId === userId && queue.pending.length > 0;
}

// ── The wall ──────────────────────────────────────────────────────────────

/** The signed-in person's wall, read again when a find arrives. */
export function useAchievements() {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const refresh = useArrivals();
  return useRead(userId === null ? null : '/me/achievements', AchievementsView, {
    key: userId ?? '',
    refresh,
  });
}

// ── Where an open one is tried ────────────────────────────────────────────

/**
 * Each feature as this client's screen (§11: "a link to the feature"). The
 * wire names a feature, not a path, because a path is one client's. A
 * league's screen opens in the person's first league; with none, starting
 * one is where every league feature begins.
 */
export function featureLink(
  feature: AchievementFeature,
  leagueId: string | null,
): { label: string; to: string } {
  const inLeague = (label: string, rest: string) =>
    leagueId === null
      ? { label: 'Start a league', to: '/leagues/new' }
      : { label, to: `/l/${encodeURIComponent(leagueId)}${rest}` };
  switch (feature) {
    case 'create-league':
      return { label: 'Start a league', to: '/leagues/new' };
    case 'join-league':
      return { label: 'Join a league', to: '/join' };
    case 'looks':
    case 'detail-level':
      return { label: 'Open Appearance', to: '/account/appearance' };
    case 'install':
      return { label: 'Install the app', to: '/account/install' };
    case 'notifications':
      return { label: 'Open Notifications', to: '/account/notifications' };
    case 'team-settings':
      return inLeague('Open My Team', '');
    case 'league-layout':
    case 'invite':
      return inLeague('Open the League page', '/league');
    case 'draft-settings':
      return inLeague('Open the league’s settings', '/settings');
    case 'draft-prep':
      return inLeague('Open your draft priorities', '/priorities');
    case 'waivers':
      return inLeague('Open Waivers', '/waivers');
    case 'trades':
      return inLeague('Open Trades', '/trades');
    case 'rules':
      return inLeague('Open the Rules page', '/rules');
    case 'chat':
      return inLeague('Open your league', '');
    case 'lineup':
      return inLeague('Open your lineup', '/lineup');
    case 'record-book':
      return inLeague('Open the record book', '/history');
    case 'draft-recap':
      return inLeague('Open the draft recap', '/draft-recap');
    case 'trophy-room':
      return inLeague('Open Awards', '/awards');
  }
}
